import React, { useState } from 'react';
import bcrypt from 'bcryptjs';

interface RegisterScreenProps {
  onRegister: (user: any, token: string) => void;
  onSwitchToLogin: () => void;
}

const RegisterScreen: React.FC<RegisterScreenProps> = ({ onRegister, onSwitchToLogin }) => {
  // フォーム状態
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  
  // UI状態
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState('');

  const API_URL = 'https://voicechatapi.kasaneko.com';

  // プロフィール画像アップロード
  const handleUploadProfileImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('画像サイズは5MB以下にしてください');
        return;
      }

      try {
        setUploading(true);

        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64Data = event.target?.result as string;

          const response = await fetch(`${API_URL}/api/upload/profile-image-base64`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageBase64: base64Data })
          });

          const data = await response.json();

          if (data.success) {
            const fullImageUrl = `${API_URL}${data.imageUrl}`;
            setProfileImage(fullImageUrl);
            alert('✅ 画像をアップロードしました！');
          } else {
            alert(data.error || '画像アップロードに失敗しました');
          }

          setUploading(false);
        };

        reader.onerror = () => {
          alert('画像の読み込みに失敗しました');
          setUploading(false);
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('画像アップロードエラー:', error);
        alert('画像アップロード中にエラーが発生しました');
        setUploading(false);
      }
    };

    input.click();
  };

  // パスワード自動生成
  const generatePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*(),.?';
    
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 0; i < 6; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // シャッフル
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setPassword(password);
    setConfirmPassword(password);
  };

  // 仮登録（認証メール送信）
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // パスワード一致チェック
    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    // パスワードポリシーチェック
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUppercase) {
      setError('パスワードには大文字（A-Z）が必要です');
      return;
    }
    if (!hasLowercase) {
      setError('パスワードには小文字（a-z）が必要です');
      return;
    }
    if (!hasNumber) {
      setError('パスワードには数字（0-9）が必要です');
      return;
    }
    if (!hasSymbol) {
      setError('パスワードには記号（!@#$%など）が必要です');
      return;
    }

    try {
      setIsLoading(true);

      // パスワードをハッシュ化
      const hashedPassword = await bcrypt.hash(password, 10);

      // 仮登録（認証メール送信）
      const response = await fetch(`${API_URL}/api/email-verification/send-verification-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          username,
          password: hashedPassword,
          profileImage: profileImage
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailSent(true);
        
        // 開発環境用
        if (data.verificationUrl) {
          setVerificationUrl(data.verificationUrl);
          console.log('開発環境用認証URL:', data.verificationUrl);
        }
        
        alert('✉️ 認証メールを送信しました！\nメールに記載されたリンクをクリックして認証を完了してください。');
      } else {
        setError(data.error || '登録に失敗しました');
      }
    } catch (error: any) {
      console.error('登録エラー:', error);
      setError('登録中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  // メール送信済み画面
  if (emailSent) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: 'Arial, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '60px 40px',
          borderRadius: '20px',
          boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>
            ✉️
          </div>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '28px', color: '#333' }}>
            認証メールを送信しました
          </h2>
          <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            <strong>{email}</strong> に認証メールを送信しました。<br />
            メールに記載されたリンクをクリックして、<br />
            アカウント認証を完了してください。
          </p>

          {verificationUrl && (
            <div style={{
              background: '#e3f2fd',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px',
              fontSize: '12px',
              color: '#1976d2',
              wordBreak: 'break-all'
            }}>
              <strong>開発環境用:</strong><br />
              <a href={verificationUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                {verificationUrl}
              </a>
            </div>
          )}

          <p style={{ fontSize: '14px', color: '#999', marginBottom: '30px' }}>
            メールが届かない場合は、迷惑メールフォルダをご確認ください。
          </p>

          <button
            onClick={onSwitchToLogin}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            ← ログイン画面に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{
          textAlign: 'center',
          marginBottom: '30px',
          color: '#333',
          fontSize: '28px'
        }}>
          新規登録
        </h2>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '5px',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleRegister}>
          {/* プロフィール画像（任意） */}
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: profileImage 
                ? `url(${profileImage}) center/cover` 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              margin: '0 auto 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              border: '3px solid #f0f0f0'
            }}>
              {!profileImage && '👤'}
            </div>
            
            <button
              type="button"
              onClick={handleUploadProfileImage}
              disabled={uploading}
              style={{
                background: uploading ? '#ccc' : 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                border: '2px solid rgba(102, 126, 234, 0.3)',
                padding: '8px 16px',
                borderRadius: '15px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {uploading ? 'アップロード中...' : '📷 プロフィール画像（任意）'}
            </button>
          </div>

          {/* メールアドレス */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#555',
              fontWeight: 'bold'
            }}>
              📧 メールアドレス *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="example@email.com"
            />
          </div>

          {/* ユーザー名 */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#555',
              fontWeight: 'bold'
            }}>
              👤 ユーザー名 *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="ユーザー名を入力"
            />
          </div>

          {/* パスワード */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#555',
              fontWeight: 'bold'
            }}>
              🔒 パスワード *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '100px',
                  border: '2px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="パスワードを入力（8文字以上）"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '50px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
              <button
                type="button"
                onClick={generatePassword}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#667eea',
                  fontWeight: 'bold'
                }}
                title="自動生成"
              >
                🎲
              </button>
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
              ※ 大文字、小文字、数字、記号をすべて含む8文字以上
            </div>
          </div>

          {/* パスワード確認 */}
          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#555',
              fontWeight: 'bold'
            }}>
              🔒 パスワード確認 *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="パスワードを再入力"
            />
          </div>

          {/* 登録ボタン */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '15px',
              background: isLoading ? '#ccc' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background 0.3s'
            }}
          >
            {isLoading ? '認証メール送信中...' : '📧 認証メールを送信'}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          color: '#666'
        }}>
          既にアカウントをお持ちの方は{' '}
          <button
            onClick={onSwitchToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '16px'
            }}
          >
            ログイン
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;
